import React, { useEffect, useState, createElement } from 'react';
import { Navbar } from './components/Navbar';
import { HeroSection } from './components/HeroSection';
import { ProblemSection } from './components/ProblemSection';
import { SolutionSection } from './components/SolutionSection';
import { HowItWorksSection } from './components/HowItWorksSection';
import { PricingSection } from './components/PricingSection';
import { ContactForm } from './components/ContactForm';
import { KraaftComparisonSection } from './components/KraaftComparisonSection';
import { WhatsAppButton } from './components/WhatsAppButton';
import { Footer } from './components/Footer';
import { ErrorBoundary } from './components/ErrorBoundary';
import { BrandBook } from './pages/BrandBook';
import { PlanoTool } from './pages/PlanoTool';
import { PinFotos } from './pages/PinFotos';
// Safely read a URL search param without ever throwing
function getSearchParam(name: string): string | null {
  try {
    if (typeof window === 'undefined') return null;
    const url = new URL(window.location.href);
    return url.searchParams.get(name);
  } catch (err) {
    console.error('getSearchParam error:', err);
    return null;
  }
}
// Safely read the URL hash
function getHash(): string {
  try {
    if (typeof window === 'undefined') return '';
    return window.location.hash || '';
  } catch {
    return '';
  }
}
export function App() {
  // Read wa_id once on mount (avoids re-evaluating on every render)
  const [waId] = useState<string | null>(() => getSearchParam('wa_id'));
  const [hash, setHash] = useState<string>(() => getHash());
  useEffect(() => {
    const onHashChange = () => setHash(getHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  // SEO: set document title and meta description so crawlers (incl. AI) see real content
  useEffect(() => {
    try {
      document.title = 'Obra Digital — Fotos de obra ordenadas por plano y fecha';
      const ensureMeta = (name: string, content: string, isProperty = false) => {
        const attr = isProperty ? 'property' : 'name';
        let tag = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute(attr, name);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
      };
      const description = 'Obra Digital ordena las fotos de tu obra por plano, fecha y autor. Sin apps nuevas, sin capacitación. Desde UF 10 al mes por obra.';
      ensureMeta('description', description);
      ensureMeta('robots', 'index,follow');
      ensureMeta('og:title', 'Obra Digital', true);
      ensureMeta('og:description', description, true);
      ensureMeta('og:type', 'website', true);
      ensureMeta('twitter:card', 'summary_large_image');
      ensureMeta('twitter:title', 'Obra Digital');
      ensureMeta('twitter:description', description);
      // Viewport (mobile)
      let viewport = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
      if (!viewport) {
        viewport = document.createElement('meta');
        viewport.setAttribute('name', 'viewport');
        document.head.appendChild(viewport);
      }
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover');
    } catch (err) {
      console.error('SEO setup error:', err);
    }
  }, []);
  // Internal tools — direct deep link with ?wa_id=
  if (waId) {
    return <ErrorBoundary data-id="element-0">
        <PinFotos data-id="element-1" />
      </ErrorBoundary>;
  }
  // Hidden hash routes
  if (hash === '#pinfotos') {
    return <ErrorBoundary data-id="element-2">
        <PinFotos data-id="element-3" />
      </ErrorBoundary>;
  }
  if (hash === '#plano') {
    return <ErrorBoundary data-id="element-4">
        <PlanoTool data-id="element-5" />
      </ErrorBoundary>;
  }
  if (hash === '#brand') {
    return <ErrorBoundary data-id="element-6">
        <BrandBook data-id="element-7" />
      </ErrorBoundary>;
  }
  // Public landing page — always available to everyone
  return <ErrorBoundary data-id="element-8">
      <div className="min-h-screen bg-white selection:bg-blue-100 selection:text-blue-900" data-id="element-9">
        <Navbar data-id="element-10" />

        <main data-id="element-11">
          <HeroSection data-id="element-12" />
          <ProblemSection data-id="element-13" />
          <SolutionSection data-id="element-14" />
          <HowItWorksSection data-id="element-15" />
          <KraaftComparisonSection data-id="element-16" />
          <PricingSection data-id="element-17" />
          <ContactForm data-id="element-18" />
        </main>

        <Footer data-id="element-19" />
        <WhatsAppButton data-id="element-20" />
      </div>
    </ErrorBoundary>;
}